import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import AdminModal from '../../components/AdminModal';
import {
  StandardTable, StandardTableBody, StandardTableCell, StandardTableHeader,
  StandardTableHeaderCell, StandardTableRow,
} from '../../../components/StandardTable';
import { DownloadIcon, UploadIcon, WarningIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import { coverageLabel } from '../../../constants/candidateProfiles';
import { apiError, labelClasses } from './InitiativeDetailPage';
import {
  bulkProfileImport, coverageCsv, fetchCoverage, groupBulkResults, selectBundleFiles,
  type BulkProfileResponse, type BulkProfileStatus,
} from '../../lib/candidateProfiles';

interface Props {
  electionYear: number;
}

const STATUS_TONE: Record<BulkProfileStatus, 'positive' | 'caution' | 'neutral' | 'info' | 'negative'> = {
  matched: 'info',
  imported: 'positive',
  unmatched: 'neutral',
  ambiguous: 'caution',
  conflict: 'caution',
  failed: 'negative',
};
const STATUS_LABEL: Record<BulkProfileStatus, string> = {
  matched: 'Ready',
  imported: 'Imported',
  unmatched: 'Unmatched',
  ambiguous: 'Ambiguous',
  conflict: 'Conflict',
  failed: 'Failed',
};

export const coverageQueryKey = (electionYear: number) => ['admin-elections', 'profile-coverage', electionYear];

const downloadText = (filename: string, text: string) => {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Candidate profiles on a cycle (CANDIDATE_PROFILES_PRD.md §5.4.2): per contest
 * how many candidacies are on the ballot, how many carry a profile, how many
 * are published, at which tier — and, in red, candidacies with no contest at
 * all, whose profiles nobody can reach. The folder import lands here because
 * a bundle folder covers a contest, and the CSV is the October war-room sheet.
 */
const CandidateProfileCoverageCard: React.FC<Props> = ({ electionYear }) => {
  const queryClient = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const coverage = useQuery({
    queryKey: coverageQueryKey(electionYear),
    queryFn: () => fetchCoverage(electionYear),
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: coverageQueryKey(electionYear) });
    queryClient.invalidateQueries({ queryKey: ['admin-elections'] });
  };

  const rows = coverage.data?.contests ?? [];
  const unlinked = coverage.data?.unlinked ?? [];
  const unlinkedProfiles = unlinked.reduce((n, u) => n + u.profiles, 0);
  const unlinkedCandidacies = unlinked.reduce((n, u) => n + u.candidacies, 0);
  const unpaired = coverage.data?.unpaired_lt_governors ?? [];

  return (
    <div className="card p-5">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Candidate profiles</h4>
          <HelpTrigger contentKey="candidate-profile-import" size="sm" ariaLabel="How candidate profile bundles are imported" />
        </span>
        <span className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            icon={DownloadIcon}
            disabled={!coverage.data}
            onClick={() => coverage.data && downloadText(`candidate-profile-coverage-${electionYear}.csv`, coverageCsv(coverage.data))}
          >
            Export coverage (CSV)
          </Button>
          <Button variant="secondary" size="sm" icon={UploadIcon} onClick={() => setImportOpen(true)}>
            Import profiles (folder)
          </Button>
        </span>
      </div>

      {coverage.isError ? (
        <p className="text-sm text-error-600 dark:text-error-400">Couldn&apos;t load profile coverage.</p>
      ) : coverage.isLoading ? (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading coverage…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No contests in this cycle yet.</p>
      ) : (
        <StandardTable>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell>Contest</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Candidates</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Loaded</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Published</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Tier</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {rows.map((c) => (
              <StandardTableRow key={c.contest_id}>
                <StandardTableCell>
                  <span className="text-text-primary dark:text-text-dark-primary">{c.display_label}</span>
                  <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">{c.election_type}</span>
                </StandardTableCell>
                <StandardTableCell>{c.candidates}</StandardTableCell>
                <StandardTableCell>{c.loaded}</StandardTableCell>
                <StandardTableCell>
                  {c.published}
                  {c.loaded > 0 && c.published < c.loaded && (
                    <span className="ml-1 text-xs text-warning-700 dark:text-warning-300">({c.loaded - c.published} draft)</span>
                  )}
                </StandardTableCell>
                <StandardTableCell>
                  {c.tiers.length === 0 ? (
                    <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>
                  ) : c.tiers.length > 1 ? (
                    <Chip label={`Mixed: ${c.tiers.map(coverageLabel).join(' / ')}`} tone="negative" size="sm" />
                  ) : (
                    <Chip label={coverageLabel(c.tiers[0])} tone="info" size="sm" />
                  )}
                </StandardTableCell>
              </StandardTableRow>
            ))}
          </StandardTableBody>
        </StandardTable>
      )}

      {unlinkedCandidacies > 0 && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">
          <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {unlinkedCandidacies} candidac{unlinkedCandidacies === 1 ? 'y' : 'ies'} in {electionYear} {unlinkedCandidacies === 1 ? 'is' : 'are'} not linked to any contest
            {unlinkedProfiles > 0 ? `, ${unlinkedProfiles} of them with a profile nobody can reach` : ''}:{' '}
            {unlinked.map((u) => `${u.office_sought} (${u.candidacies})`).join(', ')}. Link them from each person&apos;s Roles tab.
          </span>
        </p>
      )}

      {unpaired.length > 0 && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">
          <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {unpaired.length} Lieutenant Governor candidac{unpaired.length === 1 ? 'y has' : 'ies have'} no running mate and{' '}
            {unpaired.length === 1 ? 'is' : 'are'} on no ballot line:{' '}
            {unpaired.map((u, i) => (
              <React.Fragment key={u.candidate_id}>
                {i > 0 ? ', ' : ''}
                <Link to={`/admin-next/people/${u.person_id}`} className="underline">{u.name}</Link>
                {u.party ? ` (${u.party})` : ''}
              </React.Fragment>
            ))}
            . Pick the governor on the candidacy&apos;s Edit form (person&apos;s Roles tab).
          </span>
        </p>
      )}

      <CandidateProfileBulkImportModal
        electionYear={electionYear}
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={refresh}
      />
    </div>
  );
};

interface BulkProps {
  electionYear: number;
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

/**
 * Import candidate profiles — a whole folder at once (§5.4.2). One dialog (the
 * folder itself, or all its files), a preflight grouped by race showing what
 * each bundle would do and whether the race is at one depth, then one Import
 * that runs each matched candidate in its own transaction. A bad bundle never
 * blocks the others.
 */
export const CandidateProfileBulkImportModal: React.FC<BulkProps> = ({ electionYear, isOpen, onClose, onImported }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [skipped, setSkipped] = useState(0);
  const [result, setResult] = useState<BulkProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setFiles([]); setSkipped(0); setResult(null); setError(null); };
  const close = () => { reset(); onClose(); };

  const onFiles = (list: FileList | null) => {
    const all = Array.from(list ?? []);
    const bundles = selectBundleFiles(all);
    setFiles(bundles);
    setSkipped(all.length - bundles.length);
    setResult(null);
    setError(bundles.length ? null : 'No profile files in that selection — expected *-fields.json and *-record-* files.');
  };

  const preview = useMutation({
    mutationFn: () => bulkProfileImport(electionYear, files, true),
    onSuccess: (data) => { setResult(data); setError(null); },
    onError: (err: any) => setError(apiError(err, 'Could not preview the folder')),
  });

  const run = useMutation({
    mutationFn: () => bulkProfileImport(electionYear, files, false),
    onSuccess: (data) => {
      setResult(data);
      const imported = data.results.filter((r) => r.status === 'imported').length;
      const failed = data.results.filter((r) => r.status === 'failed').length;
      toast[failed ? 'error' : 'success'](`${imported} imported${failed ? `, ${failed} failed` : ''} — publish each race when it is reviewed`);
      onImported();
    },
    onError: (err: any) => setError(apiError(err, 'Import failed')),
  });

  const ready = result?.results.filter((r) => r.status === 'matched').length ?? 0;
  const done = result && !result.dry_run;

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={close}
      title="Import candidate profiles"
      subtitle={`${electionYear} candidacies`}
      size="xl"
      footer={<>
        <Button variant="secondary" size="sm" onClick={close}>{done ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button variant="primary" size="sm" onClick={() => preview.mutate()} disabled={files.length === 0 || preview.isPending} loading={preview.isPending}>
            Preview
          </Button>
        )}
        {result?.dry_run && (
          <Button variant="primary" size="sm" onClick={() => run.mutate()} disabled={ready === 0 || run.isPending} loading={run.isPending}>
            Import {ready} profile{ready === 1 ? '' : 's'}
          </Button>
        )}
      </>}
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Choose the profile folder (or select all of its files). Each bundle is matched to a
            {electionYear} candidacy by its SOS or FEC id and election type; packets and scripts are ignored.
          </p>
          <HelpTrigger contentKey="candidate-profile-import" size="sm" ariaLabel="Help for importing candidate profiles" />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="profile-folder" className={labelClasses}>Choose a folder</label>
            <input
              id="profile-folder"
              type="file"
              multiple
              className="input w-full"
              onChange={(e) => onFiles(e.target.files)}
              {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
            />
          </div>
          <div>
            <label htmlFor="profile-multi" className={labelClasses}>…or choose files</label>
            <input
              id="profile-multi"
              type="file"
              multiple
              accept=".json,.pdf"
              className="input w-full"
              onChange={(e) => onFiles(e.target.files)}
            />
          </div>
        </div>
        {files.length > 0 && (
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            {files.length} profile file{files.length === 1 ? '' : 's'} selected{skipped ? `, ${skipped} other file${skipped === 1 ? '' : 's'} ignored` : ''}.
          </p>
        )}

        {error && (
          <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
        )}

        {result && groupBulkResults(result).map(({ summary, results }) => (
          <div key={summary.contest_id ?? summary.label}>
            <div className="mb-1 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-text-primary dark:text-text-dark-primary">{summary.label}</span>
              {summary.tiers.length > 0 && (
                <Chip
                  label={summary.mixed_tier ? `Mixed tier: ${summary.tiers.map(coverageLabel).join(' / ')}` : coverageLabel(summary.tiers[0])}
                  tone={summary.mixed_tier ? 'negative' : 'info'}
                  size="sm"
                />
              )}
              <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                {[
                  summary.matched ? `${summary.matched} ready` : null,
                  summary.imported ? `${summary.imported} imported` : null,
                  summary.unmatched ? `${summary.unmatched} unmatched` : null,
                  summary.ambiguous ? `${summary.ambiguous} ambiguous` : null,
                  summary.conflict ? `${summary.conflict} conflict` : null,
                  summary.failed ? `${summary.failed} failed` : null,
                ].filter(Boolean).join(' · ')}
              </span>
              {summary.unlinked > 0 && (
                <Chip label={`${summary.unlinked} not linked to a contest`} tone="negative" size="sm" />
              )}
            </div>
            <StandardTable>
              <StandardTableHeader>
                <StandardTableRow>
                  <StandardTableHeaderCell>Bundle</StandardTableHeaderCell>
                  <StandardTableHeaderCell columnType="fit">Version</StandardTableHeaderCell>
                  <StandardTableHeaderCell>Candidate</StandardTableHeaderCell>
                  <StandardTableHeaderCell columnType="fit">Files</StandardTableHeaderCell>
                  <StandardTableHeaderCell columnType="fit">Result</StandardTableHeaderCell>
                </StandardTableRow>
              </StandardTableHeader>
              <StandardTableBody>
                {results.map((r) => (
                  <StandardTableRow key={r.prefix}>
                    <StandardTableCell>
                      <div className="font-medium text-text-primary dark:text-text-dark-primary">{r.candidate_label ?? r.prefix}</div>
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{r.prefix}</div>
                    </StandardTableCell>
                    <StandardTableCell>{r.version ?? '—'}</StandardTableCell>
                    <StandardTableCell>
                      {r.candidate ? (
                        <>
                          <div className="text-text-primary dark:text-text-dark-primary">{r.candidate.name}</div>
                          <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            {r.candidate.contest_label ?? r.candidate.office_sought ?? '—'} · {r.candidate.status ?? '—'}
                            {r.candidate.publication_status ? ` · ${r.candidate.publication_status}` : ''}
                          </div>
                        </>
                      ) : (
                        <span className="text-text-tertiary dark:text-text-dark-tertiary">
                          {r.identity?.sos_candidate_id ? `SOS ${r.identity.sos_candidate_id}` : r.identity?.fec_candidate_id ? `FEC ${r.identity.fec_candidate_id}` : '—'}
                        </span>
                      )}
                    </StandardTableCell>
                    <StandardTableCell>
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        {[r.files.fields && 'fields', r.files.record && 'record', r.files.pdf && 'pdf'].filter(Boolean).join(' · ')}
                      </span>
                    </StandardTableCell>
                    <StandardTableCell>
                      <Chip label={STATUS_LABEL[r.status]} tone={STATUS_TONE[r.status]} size="sm" />
                      {r.reason && <div className="mt-1 max-w-xs text-xs text-text-tertiary dark:text-text-dark-tertiary">{r.reason}</div>}
                      {r.status === 'matched' && r.preflight?.warnings.length ? (
                        <div className="mt-1 max-w-xs text-xs text-text-tertiary dark:text-text-dark-tertiary">{r.preflight.warnings[0]}</div>
                      ) : null}
                    </StandardTableCell>
                  </StandardTableRow>
                ))}
              </StandardTableBody>
            </StandardTable>
          </div>
        ))}
        {result?.ignored.length ? (
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Ignored: {result.ignored.join(', ')}</p>
        ) : null}
      </div>
    </AdminModal>
  );
};

export default CandidateProfileCoverageCard;
